import { TFunction } from 'i18next'
import { IQuestion } from '../api/types/checklist'

// question types that can have an expected answer ("Erwartete Antwort")
export const EXPECTATION_TYPES = ['checkbox', 'real_checkbox', 'number', 'rating', 'dropdown', 'text']

// Expected answer of a text question, stored as JSON in expected_value. Same rules as the backend
// (TextExpectation): case and surrounding spaces don't matter; in a "format" example a letter stands
// for any letter, a digit for any digit, everything else must be typed as shown.
export type TextExpectationMode = 'one_of' | 'contains' | 'not_contains' | 'format'
export const TEXT_EXPECTATION_MODES: TextExpectationMode[] = ['one_of', 'contains', 'not_contains', 'format']

export interface TextExpectation {
  mode: TextExpectationMode
  values: string[]
}

export function parseTextExpectation(raw: unknown): TextExpectation | null {
  try {
    const data = typeof raw === 'string' ? JSON.parse(raw) : raw
    if (!data || typeof data !== 'object') return null
    const mode = String((data as any).mode ?? '') as TextExpectationMode
    const values = (Array.isArray((data as any).values) ? (data as any).values : [])
      .map((v: unknown) => String(v ?? '').trim()).filter((v: string) => v !== '')
    if (!TEXT_EXPECTATION_MODES.includes(mode)) return null
    return { mode, values }
  } catch {
    return null
  }
}

export function formatRegExp(example: string): RegExp {
  const pattern = Array.from(example.trim()).map((char) => {
    if (/\p{L}/u.test(char)) return '\\p{L}'
    if (/\d/.test(char)) return '\\d'
    if (/\s/.test(char)) return '\\s+'
    // only syntax characters may be escaped in a unicode regular expression
    return char.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&')
  }).join('')
  return new RegExp(`^${pattern}$`, 'iu')
}

// true when the answer fits the expectation (also when nothing is set up yet)
export function textMatches(expectation: TextExpectation | null, value: string): boolean {
  if (!expectation || expectation.values.length === 0) return true
  const answer = value.trim().toLowerCase()
  const wanted = expectation.values.map((v) => v.toLowerCase())
  switch (expectation.mode) {
    case 'one_of': return wanted.includes(answer)
    case 'contains': return wanted.some((v) => answer.includes(v))
    case 'not_contains': return !wanted.some((v) => answer.includes(v))
    case 'format': return expectation.values.some((v) => formatRegExp(v).test(value.trim()))
    default: return true
  }
}

function toNumber(text: unknown): number | null {
  if (text === null || text === undefined || text === '') return null
  const value = Number(String(text).trim().replace(',', '.'))
  return Number.isFinite(value) ? value : null
}

// Same rule as the backend's ResultDeviation for the expected answer, so the checklist can mark
// a deviation right away (also offline). The saved result's `deviation` flag stays the reference;
// it also includes "count as error" rules.
export function isExpectedDeviation(question: IQuestion, value: string | null | undefined): boolean {
  if (!question?.expected_enabled || value === null || value === undefined) return false
  const text = String(value).trim()
  if (text === '' || text === 'n/a') return false

  switch (question.question_type) {
    case 'checkbox':
    case 'real_checkbox':
      return !!question.expected_value && text !== question.expected_value
    case 'number':
    case 'rating': {
      const number = toNumber(text)
      if (number === null) return false
      const min = toNumber(question.expected_min)
      const max = toNumber(question.expected_max)
      return (min !== null && number < min) || (max !== null && number > max)
    }
    case 'text':
      return !textMatches(parseTextExpectation(question.expected_value), text)
    case 'dropdown': {
      const correct = (question.question_dropdown_items ?? []).filter((x) => x.is_correct).map((x) => String(x.id))
      const selected = text.split(',').filter(Boolean)
      return correct.length > 0 && selected.some((id) => !correct.includes(id))
    }
    default:
      return false
  }
}

function formatNumber(value: unknown) {
  const number = toNumber(value)
  return number === null ? '' : number.toLocaleString('de-DE')
}

// "erwartet: Ja", "Soll: höchstens 7 °C", "Soll: 2 bis 7 °C", "erwartet: Gut, Sehr gut"
export function describeExpectation(question: IQuestion, t: TFunction): string | null {
  if (!question?.expected_enabled) return null
  const unit = question.unit ? ` ${question.unit}` : ''
  switch (question.question_type) {
    case 'checkbox':
      if (!question.expected_value) return null
      return t('expectedAnswer.expected', { value: question.expected_value === 'true' ? t('Yes') : t('No') })
    case 'real_checkbox':
      if (!question.expected_value) return null
      return t('expectedAnswer.expected', { value: question.expected_value === 'true' ? t('expectedAnswer.checked') : t('expectedAnswer.unchecked') })
    case 'number':
    case 'rating': {
      const min = formatNumber(question.expected_min)
      const max = formatNumber(question.expected_max)
      if (min && max) return t('expectedAnswer.between', { min, max, unit })
      if (max) return t('expectedAnswer.atMost', { max, unit })
      if (min) return t('expectedAnswer.atLeast', { min, unit })
      return null
    }
    case 'text': {
      const expectation = parseTextExpectation(question.expected_value)
      if (!expectation || expectation.values.length === 0) return null
      return t(`expectedAnswer.text.describe.${expectation.mode}`, { value: expectation.values.join(', ') })
    }
    case 'dropdown': {
      const correct = (question.question_dropdown_items ?? []).filter((x) => x.is_correct).map((x) => x.title)
      return correct.length ? t('expectedAnswer.expected', { value: correct.join(', ') }) : null
    }
    default:
      return null
  }
}
